import type { AnimationClip, Object3D } from "three";

/**
 * AnimationMixer introspection.
 *
 * Animation is structurally INVISIBLE to every other walk in the editor: clips
 * live on the loader result (`gltf.animations`), never on a node, and they are
 * absent from `parser.associations` — so neither the object-graph traversal nor
 * the glTF association map can reach them. The mixer playing a clip is the only
 * thing that knows "clip X animates rig Y".
 *
 * Duck-typed throughout, because the mixer is a HOST-page object (the chrome
 * build injects the editor into pages that own their own three instance):
 * `getRoot()` is public API, `_actions[]._clip` is internal but stable across
 * three versions and the only way to enumerate a mixer's clips.
 */

/** The object an AnimationMixer animates, or null for anything unreadable. */
export function getMixerRoot(mixer: object): object | null {
    try {
        const root = (mixer as { getRoot?(): object | undefined }).getRoot?.();
        return root && typeof root === "object" ? root : null;
    }
    catch {
        return null;
    }
}

/**
 * The clips a mixer has actions for. Only clips a `clipAction()` was created
 * for — a clip that was loaded but never played has no action and is therefore
 * not listed here (it is still reachable through its origin model).
 */
export function getMixerClips(mixer: object): AnimationClip[] {
    const clips: AnimationClip[] = [];
    try {
        const actions = (mixer as { _actions?: unknown[] })._actions;
        if (!Array.isArray(actions)) return clips;
        for (const action of actions) {
            const clip = (action as { _clip?: AnimationClip })?._clip;
            if (clip && typeof clip === "object" && !clips.includes(clip)) clips.push(clip);
        }
    }
    catch {
        // a broken mixer must never break the caller's walk
    }
    return clips;
}

/** True if `root` is one of `objects`, or an ancestor of any of them. */
export function mixerRootCovers(root: object, objects: ReadonlySet<object>): boolean {
    if (objects.has(root)) return true;
    const node = root as Object3D;
    if (typeof node?.traverse !== "function") return false;
    let found = false;
    node.traverse(child => { if (objects.has(child)) found = true; });
    return found;
}
